import { DedupeComponent, canDeactivateDedupe } from './pages/dedupe/dedupe.component';
import { CampaignsWorkspaceComponent } from './pages/leads/campaigns/campaigns-workspace.component';
import { CampaignDetailComponent } from './pages/leads/campaigns/campaign-detail.component';
import { LEGACY_PIPELINE_REDIRECTS } from './app-routing.legacy';
import { NgModule } from '@angular/core';
import { UiGalleryComponent } from './pages/ui-gallery/ui-gallery.component';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './guards/auth.guard';
import { PermissionGuard } from './guards/permission.guard';
import { UnsavedChangesGuard } from './guards/unsaved-changes.guard';

import { FilesComponent } from './pages/files/files.component';

// Phase 02-03: Contracts
import { PendingContractsComponent } from './pages/crm/contracts/pending-contracts.component';

// Phase 01-01: Identity & Auth
import { ResetPasswordComponent } from './pages/auth/reset-password/reset-password.component';

// Configuration
import { CustomerTypeConfigComponent } from './pages/configuration/catalogs/customer-type-config.component';
import { LeadStageConfigComponent } from './pages/configuration/catalogs/lead-stage-config.component';
import { BillingStatusConfigComponent } from './pages/configuration/catalogs/billing-status-config.component';
import { PricingAreaConfigComponent } from './pages/configuration/catalogs/pricing-area-config.component';
import { CommPrefConfigComponent } from './pages/configuration/catalogs/comm-pref-config.component';
import { TransactionCodeConfigComponent } from './pages/crm/transaction-codes/transaction-code-config.component';
import { MaterialTypeConfigComponent } from './pages/configuration/catalogs/material-type-config.component';
import { VehicleTypeConfigComponent } from './pages/configuration/catalogs/vehicle-type-config.component';
import { DepotConfigComponent } from './pages/configuration/catalogs/depot-config.component';
// Phase 05-01
import { CompanyConfigurationComponent } from './pages/configuration/company/company-configuration.component';
// Phase 06-01 Security / Audit Log
import { AuditLogComponent } from './pages/configuration/audit-log/audit-log.component';
import { JobSchedulesComponent } from './pages/configuration/job-schedules/job-schedules.component';

import { CrmFollowUpsComponent } from './pages/crm/follow-ups/follow-ups.component';
import { SignPageComponent } from './pages/crm/contracts/sign/sign-page.component';
import { CrmCaseDetailComponent } from './pages/crm/cases/case-detail.component';
import { CrmCasesWorkspaceComponent } from './pages/crm/cases/cases-workspace.component';
import { LeadPageComponent } from './pages/leads/board/lead-page.component';
import { LeadsDefaultViewGuard } from './pages/leads/board/leads-default-view.guard';
import { LeadsWorkspaceComponent } from './pages/leads/board/leads-workspace.component';
import { RollOffWorkspaceComponent } from './pages/leads/rolloff/rolloff-workspace.component';
import { LeadsV2PageComponent } from './pages/leads/smart-workflow/leads-v2-page.component';
import { LeadsV2WorkflowEditorComponent } from './pages/leads/smart-workflow/leads-v2-workflow-editor.component';
import { SmartWorkflowWorkspaceComponent } from './pages/leads/smart-workflow/smart-workflow-workspace.component';
import { CommandCenterComponent } from './pages/leads/command-center/command-center.component';
import { LeadsOverviewComponent } from './pages/leads/overview/leads-overview.component';
import { TerritoriesComponent } from './pages/leads/territory/territories.component';
import { SalesRouteComponent } from './pages/leads/sales-route/sales-route.component';
import { NurtureComponent } from './pages/leads/nurture/nurture.component';
import { CustomerPageComponent } from './pages/crm/customers/customer-page.component';
import { CustomerWorkspaceComponent } from './pages/crm/customers/customer-workspace.component';
import { WorkspaceRootComponent } from './components/workspace-root/workspace-root.component';
import { HomeViewComponent } from './pages/home/home-view.component';
import { overviewChildRoutes, overviewRouteData, redirectLegacyOverviewView } from './pages/home/overview-routing';
import { ReportTemplatesWorkspaceComponent } from './pages/home/report-templates/report-templates-workspace.component';
import { ReportTemplateDetailComponent } from './pages/home/report-templates/report-template-detail.component';
import { DashboardViewComponent } from './pages/dashboard/dashboard-view.component';
import { SelectionWorkspaceComponent } from './pages/map/selection/selection-workspace.component';
import { ProspectWorkspaceComponent } from './pages/map/prospect/prospect-workspace.component';
import { SELECTION_WORKSPACE_PATH } from './services/workspace-route';

// Exported so the route table can be asserted directly (see
// app-routing.redirects.spec.ts); the app itself still reads router.config.
export const routes: Routes = [
  // Root '/': the map workspace. Renders nothing (the persistent <app-map> + the
  // sidebar's view-tab overlays are the workspace); exists so '/' is a real route.
  { path: '', component: WorkspaceRootComponent, data: { title: 'Map' } },

  // Phase 01-01: Forgot/reset password (public — reached from an emailed link)
  { path: 'reset-password', component: ResetPasswordComponent },

  // Phase 02-03: Home + pending contracts
  // The view tabs are routes like any other card, so a refresh (or a shared
  // link) lands back on the same surface instead of dropping to the map.
  { path: 'home', redirectTo: 'overview', pathMatch: 'full' },
  // Report templates are entered from Overview, in the Campaigns master-detail format; reports are generated from a template.
  {
    path: 'overview/templates', component: ReportTemplatesWorkspaceComponent, canActivate: [AuthGuard],
    data: { title: 'Report templates', breadcrumbRoot: '/overview' },
    children: [
      // One job per page: generating from a template and editing it are separate routes.
      { path: 'new', component: ReportTemplateDetailComponent, canActivate: [PermissionGuard], canDeactivate: [UnsavedChangesGuard],
        data: { title: 'New template', templateMode: 'edit', permission: 'Company.Manage' } },
      { path: ':id', redirectTo: ':id/generate', pathMatch: 'full' },
      { path: ':id/generate', component: ReportTemplateDetailComponent, data: { title: 'Generate report', templateMode: 'generate' } },
      { path: ':id/edit', component: ReportTemplateDetailComponent, canActivate: [PermissionGuard], canDeactivate: [UnsavedChangesGuard],
        data: { title: 'Edit template', templateMode: 'edit', permission: 'Company.Manage' } },
    ],
  },
  { path: 'overview', component: HomeViewComponent, canActivate: [AuthGuard, redirectLegacyOverviewView],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: overviewRouteData, children: overviewChildRoutes },
  // Development-only control gallery. Remove before shipping.
  { path: 'ui-gallery', component: UiGalleryComponent, data: { title: 'UI Gallery', showInDashboard: false } },
  { path: 'dashboard', component: DashboardViewComponent, canActivate: [AuthGuard], data: { title: 'Dashboard' } },
  {
    path: 'campaigns', component: CampaignsWorkspaceComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { title: 'Campaigns', icon: 'campaign', group: 'Sales', order: 3, breadcrumbGroup: false,
      description: 'Plan residential, commercial and roll-off promotions', permission: 'LeadWorkItem.View' },
    children: [
      { path: 'new', component: CampaignDetailComponent, canActivate: [AuthGuard, PermissionGuard],
        canDeactivate: [UnsavedChangesGuard], data: { title: 'New Campaign', campaignNew: true, permission: 'Lead.Manage' } },
      { path: ':id', component: CampaignDetailComponent, canActivate: [AuthGuard, PermissionGuard],
        canDeactivate: [UnsavedChangesGuard], data: { title: 'Campaign', permission: 'LeadWorkItem.View' } },
    ],
  },
  // The map selection review is ordinary routed Workspace content. `mapPage`
  // lets the shared route-owned layout expose the map by default; this page
  // owns no shell, Composer, dock mode or persisted layout state of its own.
  { path: 'places/selection', redirectTo: SELECTION_WORKSPACE_PATH, pathMatch: 'full' },
  { path: SELECTION_WORKSPACE_PATH, component: SelectionWorkspaceComponent, canActivate: [AuthGuard], data: { title: 'Selections', showInDashboard: false, mapPage: true, breadcrumbRoot: '/' } },
  {
    path: 'prospect',
    component: ProspectWorkspaceComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      title: 'Lead Discovery',
      icon: 'radar',
      group: 'Sales',
      description: 'Find potential customers on the map',
      order: 1,
      permission: 'Place.Manage',
      mapPage: true,
      breadcrumbRoot: '/',
      breadcrumbLayer: 'Lead Discovery',
      breadcrumbGroup: false,
    },
  },
  // The page is the contract list; Draft and Sent are checkboxes on it, so
  // `pending` named a default filter value rather than a resource and was wrong
  // the moment a user cleared a box. The API it reads is `/api/contracts`.
  { path: 'files', component: FilesComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Files', icon: 'folder', permission: 'File.View', showInDashboard: false, breadcrumbRoot: '/files' },
    // The open folder is part of the path (/files/<folderId>); one Files page stays mounted across folders.
    children: [{ path: '', children: [] }, { path: ':folder', children: [] }] },
  { path: 'contracts/pending', redirectTo: 'contracts', pathMatch: 'full' },
  { path: 'contracts', component: PendingContractsComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Contract Management', icon: 'description', group: 'CRM', description: 'Review and manage contracts', permission: 'Contract.Manage' } },

  // Users / Roles / API Keys are no longer routed — they live in the account-menu
  // Configuration dialog as hosted/nested components.

  // Config
  { path: 'config/customer-type',           component: CustomerTypeConfigComponent,           canActivate: [AuthGuard, PermissionGuard], data: { title: 'Customer Types', icon: 'category', group: 'Configuration', stage: 'Customers & Billing', description: 'Customer type catalog', permission: 'Config.View' } },
  { path: 'config/billing-status',          component: BillingStatusConfigComponent,          canActivate: [AuthGuard, PermissionGuard], data: { title: 'Billing Statuses', icon: 'toggle_on', group: 'Configuration', stage: 'Customers & Billing', description: 'Billing status catalog', permission: 'Config.View' } },
  { path: 'config/pricing-area',            component: PricingAreaConfigComponent,            canActivate: [AuthGuard, PermissionGuard], data: { title: 'Pricing Areas', icon: 'map', group: 'Configuration', stage: 'Customers & Billing', description: 'Pricing area catalog', permission: 'Config.View' } },
  { path: 'config/communication-preference', component: CommPrefConfigComponent,              canActivate: [AuthGuard, PermissionGuard], data: { title: 'Communication Prefs', icon: 'notifications', group: 'Configuration', stage: 'Customers & Billing', description: 'Communication preferences', permission: 'Config.View' } },
  // Transaction Codes: reachable by URL, but intentionally not surfaced as a
  // Dashboard tile (no title/group), per the dashboard cleanup.
  { path: 'config/transaction-codes', redirectTo: 'crm/config/transaction-codes', pathMatch: 'full' },
  { path: 'crm/config/transaction-codes', component: TransactionCodeConfigComponent,
    canActivate: [AuthGuard, PermissionGuard], data: { permission: 'Config.View' } },
  { path: 'config/material-types',           component: MaterialTypeConfigComponent,          canActivate: [AuthGuard, PermissionGuard], data: { title: 'Material Types', icon: 'delete', group: 'Configuration', stage: 'Shared Catalogs', description: 'Material type catalog', permission: 'Config.View' } },
  { path: 'config/vehicle-types',            component: VehicleTypeConfigComponent,           canActivate: [AuthGuard, PermissionGuard], data: { title: 'Vehicle Types', icon: 'local_shipping', group: 'Configuration', stage: 'Shared Catalogs', description: 'Vehicle type catalog', permission: 'Config.View' } },
  { path: 'config/depots',                   component: DepotConfigComponent,                  canActivate: [AuthGuard, PermissionGuard], data: { title: 'Depots', icon: 'warehouse', group: 'Configuration', stage: 'Leads & Routing', description: 'Sales route start and finish locations', permission: 'Config.View' } },

  // CRM
  // Cases were reachable at two URLs pointing at the same two components, and
  // the shorter pair carried neither a title nor a permission — so /cases was
  // the unguarded way into a list /crm/cases gated on Case.View. One path now,
  // and it keeps the guarded pair's data. The shared wrapper keeps the case
  // collection mounted when a routed detail opens in its place. The API is
  // top-level `/api/cases`.
  { path: 'crm/cases',           redirectTo: 'cases', pathMatch: 'full' },
  { path: 'crm/cases/:id',       redirectTo: 'cases/:id', pathMatch: 'full' },
  {
    path: 'cases',
    component: CrmCasesWorkspaceComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { title: 'Customer Service Cases', icon: 'support_agent', group: 'CRM', description: 'Customer service cases', permission: 'Case.View' },
    children: [
      { path: ':id', component: CrmCaseDetailComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Customer Service Case' } },
    ],
  },
  // Reads `/api/follow-ups`, which has no crm/ namespace either.
  { path: 'crm/follow-ups',      redirectTo: 'follow-ups', pathMatch: 'full' },
  { path: 'follow-ups',          component: CrmFollowUpsComponent,     canActivate: [AuthGuard, PermissionGuard], data: { title: 'Follow-ups', icon: 'event_available', group: 'CRM', description: 'Scheduled follow-ups across customers', permission: 'Customer.View' } },
  { path: 'leads/config/stages', component: LeadStageConfigComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Lead Stages', group: 'Sales', showInDashboard: false, permission: 'Config.View' } },
  // Public tokenized contract signing — no auth (the token is the credential).
  { path: 'sign/:token',         component: SignPageComponent },

  // Customers (phase-02-01)
  // Prospects retired: Leads is the single pre-Won surface and carries
  // everything that page showed. Legacy URLs redirect so
  // existing links and bookmarks keep working.
  { path: 'leads/prospects',  redirectTo: 'leads', pathMatch: 'full' },
  // Analysis is a supported Leads view. The older Tasks and Snoozed routes
  // remain registered for existing links, but are not primary navigation.
  { path: 'leads/overview',   component: LeadsOverviewComponent,    canActivate: [AuthGuard, PermissionGuard], data: { title: 'Tasks', icon: 'inbox', group: 'Sales', showInDashboard: false, description: 'The decisions waiting on you: pursue, price, or merge', order: 1, permission: 'LeadWorkItem.View' } },
  // The page is called Analysis on the switcher, in its own heading, on the
  // card's title bar and in this route's own data. "command-center" survived
  // only in the URL, so the one thing a person could copy and paste disagreed
  // with everything they could read. The old path still resolves.
  { path: 'leads/command-center', redirectTo: 'leads/analysis', pathMatch: 'full' },
  { path: 'leads/analysis',   component: CommandCenterComponent,    canActivate: [AuthGuard, PermissionGuard], data: { title: 'Analysis', icon: 'insights', group: 'Sales', showInDashboard: false, description: 'Profit-goal-first analytics: funnel, open pipeline, won/lost trend', order: 7, permission: 'LeadWorkItem.View', mapPage: true } },
  // Every literal leads/* page must be declared BEFORE the `leads` parent
  // below: the parent ends in a catch-all `:id` child, so a sibling declared
  // after it is never reached - the router reads its path segment as a lead id
  // and renders the lead page instead. That is not hypothetical: Territories,
  // Sales Route and Snoozed all sat below the parent
  // once, and every one of them opened as "View Lead".
  // canDeactivate on the two workspace pages that edit through a session: it is
  // inert until something dirty is registered, and it is the only thing standing
  // between an unsaved polygon or route change and a stray click on the sidebar.
  // mapPage on the same two: their subject is drawn on the shared map, so they
  // open beside it rather than over it. It decides only how the Workspace opens
  // when nobody has chosen yet for that route - see WorkspaceLayoutService.
  { path: 'leads/territories', component: TerritoriesComponent,        canActivate: [AuthGuard, PermissionGuard], canDeactivate: [UnsavedChangesGuard], data: { title: 'Territories', icon: 'map', group: 'Sales', showInDashboard: false, description: 'Sales boundaries, rep ownership, lead assignment & density', order: 3, permission: 'LeadWorkItem.View', mapPage: true } },
  { path: 'leads/sales-route', component: SalesRouteComponent,        canActivate: [AuthGuard, PermissionGuard], data: { title: 'Sales Route', icon: 'near_me', group: 'Sales', showInDashboard: false, description: "Today's visit route: what the patch owes, in driving order", order: 4, permission: 'LeadWorkItem.View', mapPage: true } },
  // Top level, not a child of anything: visibleGroups() indexes only the top
  // of router.config, so a page nested under another never reaches the
  // Dashboard - which is how the de-duplication pages arrived with no way in.
  { path: 'leads/nurture',    component: NurtureComponent,             canActivate: [AuthGuard, PermissionGuard], data: { title: 'Snoozed', icon: 'snooze', group: 'Sales', showInDashboard: false, description: 'Leads asleep until a date, with the reason each will wake', order: 5, permission: 'LeadPipeline.View' } },
  // permissionAny: the Board absorbed the former lead-list page, so a user
  // holding only Lead.View must not lose their entry point (the API still gates
  // each endpoint on its own permission).
  {
    path: 'leads',
    canActivate: [AuthGuard, PermissionGuard],
    data: { title: 'Leads', icon: 'view_kanban', group: 'Sales', description: 'Every lead from discovery through close', order: 2, permissionAny: ['LeadWorkItem.View', 'Lead.View'] },
    children: [
      { path: '', pathMatch: 'full', canActivate: [LeadsDefaultViewGuard], children: [] },
      {
        path: 'table',
        component: LeadsWorkspaceComponent,
        // mapPage: the leads this page lists are the pins on the map beside it.
        // See the note on the board view below - the three views share one.
        data: { leadCollectionView: 'list', mapPage: true, permissionAny: ['LeadWorkItem.View', 'Lead.View'] },
        children: [
          { path: 'duplicates', component: DedupeComponent, canActivate: [AuthGuard, PermissionGuard], canDeactivate: [canDeactivateDedupe], data: { title: 'Review duplicates', dedupeTable: 'leads', showInDashboard: false, permission: 'Lead.View' } },
          // Keep New under the collection so returning preserves list/board state.
          // The shared layout shows it in place of the collection at full width.
          // The standalone route further down stays for direct links.
          { path: 'new', component: LeadPageComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'New Lead', leadPageMode: 'new', showInDashboard: false, permission: 'Lead.Manage' } },
          { path: ':id/edit', component: LeadPageComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Edit Lead', leadPageMode: 'edit', showInDashboard: false, permission: 'Lead.Manage' } },
          { path: ':id', component: LeadPageComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Lead', leadPageMode: 'view', showInDashboard: false, permissionAny: ['LeadWorkItem.View', 'Lead.View'] } },
        ],
      },
      {
        path: 'board',
        component: LeadsWorkspaceComponent,
        /* mapPage, for the reason Territories and Sales Route carry it: the
           subject of this page is drawn on the shared map, and windowed lays an
           opaque card and a dimming backdrop over the whole of it. The leads in
           this board ARE the pins beside it.

           Declared on the three views rather than on the `leads` parent so the
           New Lead form and the standalone lead pages, which are forms rather
           than map views, keep the windowed default. */
        data: { leadCollectionView: 'board', mapPage: true, permissionAny: ['LeadWorkItem.View', 'Lead.View'] },
        children: [
          { path: 'duplicates', component: DedupeComponent, canActivate: [AuthGuard, PermissionGuard], canDeactivate: [canDeactivateDedupe], data: { title: 'Review duplicates', dedupeTable: 'leads', showInDashboard: false, permission: 'Lead.View' } },
          // Keep New under the collection so returning preserves list/board state.
          // The shared layout shows it in place of the collection at full width.
          // The standalone route further down stays for direct links.
          { path: 'new', component: LeadPageComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'New Lead', leadPageMode: 'new', showInDashboard: false, permission: 'Lead.Manage' } },
          { path: ':id/edit', component: LeadPageComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Edit Lead', leadPageMode: 'edit', showInDashboard: false, permission: 'Lead.Manage' } },
          { path: ':id', component: LeadPageComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Lead', leadPageMode: 'view', showInDashboard: false, permissionAny: ['LeadWorkItem.View', 'Lead.View'] } },
        ],
      },
      { path: 'list', pathMatch: 'full', redirectTo: 'table' },
      { path: 'list/new', pathMatch: 'full', redirectTo: 'new' },
      { path: 'list/:id/edit', pathMatch: 'full', redirectTo: 'table/:id/edit' },
      { path: 'list/:id', pathMatch: 'full', redirectTo: 'table/:id' },
      { path: 'new', component: LeadPageComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'New Lead', leadPageMode: 'new', showInDashboard: false, permission: 'Lead.Manage' } },
      { path: ':id/edit', component: LeadPageComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Edit Lead', leadPageMode: 'edit', showInDashboard: false, permission: 'Lead.Manage' } },
      { path: ':id', component: LeadPageComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Lead', leadPageMode: 'view', showInDashboard: false, permissionAny: ['LeadWorkItem.View', 'Lead.View'] } },
    ],
  },
  // Roll-off hires: the same reps working the same kind of record against a
  // second stage vocabulary, so it sits in the Leads group beside the board it
  // is a sibling of. `order: 2.5` puts it directly after Leads without
  // renumbering four routes that have nothing to do with this change; the rank
  // is only ever compared, never displayed.
  //
  // LeadPipeline.View is what the API gates the hire reads on, and deliberately
  // so - RollOffOpportunitiesController explains at length why roll-off reuses
  // the lead pipeline's keys rather than minting a RollOff pair that would be
  // granted to nobody.
  //
  // The record is a hire throughout: it carries `leadStageOutcome` of
  // open/won/lost, so "opportunity" names an early state rather than the thing
  // — a lost row is still a hire that did not happen. The model's own prose
  // already says hire, and so does the heading. The API path and the DTO keep
  // the older word; they are a contract, and a URL is not.
  { path: 'rolloff',          redirectTo: 'rolloff-hires', pathMatch: 'full' },
  {
    path: 'rolloff-hires',
    // NO component here, the way `leads` has none. The workspace template is a
    // master-detail frame with no <router-outlet>, so mounting it on the parent
    // left the `table` and `board` children with nowhere to render: the parent
    // read its own route data, found no view, fell back to the board, and the
    // Table entry in the switcher changed the URL and nothing else.
    canActivate: [AuthGuard, PermissionGuard],
    data: { title: 'Roll-off Deals', icon: 'delete_sweep', group: 'Sales', description: 'One-off dumpster rentals, from quote to fulfillment', order: 2.5, permission: 'LeadPipeline.View' },
    children: [
      // Which presentation is on screen is the URL's business, the way it is
      // for Leads: /table and /board are two routes over one workspace, so a
      // link to a filtered list is a link somebody can send. `board` is the
      // default because it is what this page has always opened as.
      { path: '', pathMatch: 'full', redirectTo: 'board' },
      {
        path: 'table',
        component: RollOffWorkspaceComponent,
        data: { rollOffCollectionView: 'list', permission: 'LeadPipeline.View' },
        children: [
          { path: 'duplicates', component: DedupeComponent, canActivate: [AuthGuard, PermissionGuard], canDeactivate: [canDeactivateDedupe], data: { title: 'Review duplicates', dedupeTable: 'rolloff-hires', showInDashboard: false, permission: 'LeadPipeline.View' } },
          { path: ':id', data: { title: 'Roll-off Deal' }, children: [] },
        ],
      },
      {
        path: 'board',
        component: RollOffWorkspaceComponent,
        data: { rollOffCollectionView: 'board', permission: 'LeadPipeline.View' },
        children: [
          { path: 'duplicates', component: DedupeComponent, canActivate: [AuthGuard, PermissionGuard], canDeactivate: [canDeactivateDedupe], data: { title: 'Review duplicates', dedupeTable: 'rolloff-hires', showInDashboard: false, permission: 'LeadPipeline.View' } },
          // Componentless on purpose: the mounted Workspace reads this id and
          // renders the detail in place of its collection. Angular still requires an
          // explicit child configuration for a componentless route.
          { path: ':id', data: { title: 'Roll-off Deal' }, children: [] },
        ],
      },
      // The address /rolloff-hires/:id has been linkable since this page
      // existed, so it is kept - as a redirect onto the default view, which is
      // what makes it open the hire now that the workspace is mounted by the
      // view routes rather than by this one. Declared last: it matches any
      // single segment, so `table` and `board` must be read first.
      { path: ':id', pathMatch: 'full', redirectTo: 'board/:id' },
    ],
  },
  // Smart workflows. `leads-v2` was a version number standing in for a
  // resource: the page it named lists workflows (New workflow, "Search
  // workflows…", edit opening the graph), and /smart-workflows/:workflowKey was
  // already the member page for the very same records. One resource now, in the
  // word the product uses on screen — the API's `dynamic-workflows` is an
  // implementation name and stays where it is.
  //
  // `new` before `:workflowKey`, or it is read as a key.
  { path: 'leads-v2/config',                 redirectTo: 'smart-workflows', pathMatch: 'full' },
  { path: 'leads-v2/workflows/new',          redirectTo: 'smart-workflows/new', pathMatch: 'full' },
  { path: 'leads-v2/workflows/:workflowKey', redirectTo: 'smart-workflows/:workflowKey/edit', pathMatch: 'full' },
  { path: 'leads-v2',                        redirectTo: 'smart-workflows', pathMatch: 'full' },
  {
    path: 'smart-workflows',
    data: { title: 'Smart Workflows', icon: 'account_tree', group: 'Smart Workflow', breadcrumbGroup: false, description: 'Manage workflows', order: 8, permission: 'DynamicTable.Manage' },
    children: [
      { path: '', pathMatch: 'full', component: LeadsV2PageComponent, canActivate: [AuthGuard, PermissionGuard] },
      { path: 'new', component: LeadsV2WorkflowEditorComponent, canActivate: [AuthGuard, PermissionGuard], canDeactivate: [UnsavedChangesGuard], data: { title: 'New workflow', create: true } },
      { path: ':workflowKey/edit', component: LeadsV2WorkflowEditorComponent, canActivate: [AuthGuard, PermissionGuard], canDeactivate: [UnsavedChangesGuard], data: { title: 'Workflow graph' } },
      { path: ':workflowKey', component: SmartWorkflowWorkspaceComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Smart Workflow' } },
    ],
  },
  {
    path: 'customers',
    canActivate: [AuthGuard, PermissionGuard],
    data: { title: 'Customers', icon: 'people', group: 'CRM', description: 'Accounts, contacts, and service addresses', order: 0, permission: 'Customer.View' },
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'table' },
      {
        path: 'table',
        component: CustomerWorkspaceComponent,
        /* mapPage, for the reason the three leads views carry it: the
           customers this page lists ARE the pins on the map beside it - the
           map's own feature list has a Customers layer, with this company's
           accounts on it - and windowed lays an opaque card and a dimming
           backdrop over the whole of that.

           This is the one thing that made /customers/table look like a
           different product from /leads/table. The two shells are already the
           same component shape; only the route data differed, so the leads
           table opened docked beside a live map and the customers table opened
           as a card over a dimmed one.

           Declared on `table` rather than on the `customers` parent so the New
           and Edit Customer forms, which are forms rather than map views, keep
           the windowed default. */
        data: { mapPage: true },
        children: [
          { path: 'duplicates', component: DedupeComponent, canActivate: [AuthGuard, PermissionGuard], canDeactivate: [canDeactivateDedupe], data: { title: 'Review duplicates', dedupeTable: 'customers', showInDashboard: false, permission: 'Customer.View' } },
          { path: 'new', pathMatch: 'full', redirectTo: '/customers/new' },
          { path: ':id/edit', component: CustomerPageComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Edit Customer', customerPageMode: 'edit', showInDashboard: false, permission: 'Customer.Manage' } },
          { path: ':id', component: CustomerPageComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Customer', customerPageMode: 'view', showInDashboard: false, permission: 'Customer.View' } },
        ],
      },
      { path: 'list', pathMatch: 'full', redirectTo: 'table' },
      { path: 'list/new', pathMatch: 'full', redirectTo: 'new' },
      { path: 'list/:id/edit', pathMatch: 'full', redirectTo: 'table/:id/edit' },
      { path: 'list/:id', pathMatch: 'full', redirectTo: 'table/:id' },
      { path: 'new', component: CustomerPageComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'New Customer', customerPageMode: 'new', showInDashboard: false, permission: 'Customer.Manage' } },
      { path: ':id/edit', component: CustomerPageComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Edit Customer', customerPageMode: 'edit', showInDashboard: false, permission: 'Customer.Manage' } },
      { path: ':id', component: CustomerPageComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Customer', customerPageMode: 'view', showInDashboard: false, permission: 'Customer.View' } },
    ],
  },

  // Phase 06-01 Security / Audit Log
  // When the retained recurring jobs run.
  { path: 'config/job-schedules', component: JobSchedulesComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { title: 'Scheduled Jobs', icon: 'schedule', group: 'Configuration', stage: 'System', description: 'When the recurring jobs run, in this company\'s local time', permission: 'JobSchedule.View' } },

  { path: 'audit-log', component: AuditLogComponent, canActivate: [AuthGuard, PermissionGuard], data: { title: 'Audit Log', icon: 'fact_check', group: 'Configuration', stage: 'System', description: 'Security & change audit', permission: 'AuditLog.View' } },

  // Preserve the old URL for bookmarks while giving company configuration a
  // canonical product-owned route.
  { path: 'account-settings', redirectTo: 'configuration/company', pathMatch: 'full' },
  { path: 'configuration/company', component: CompanyConfigurationComponent, canActivate: [AuthGuard], data: { title: 'Company Configuration', permission: 'Company.View' } },
  // Old /revenue/* URLs, declared last so they shadow nothing real.
  ...LEGACY_PIPELINE_REDIRECTS,
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { canceledNavigationResolution: 'computed' })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
